/**
 * Unified Permission System for FinnApp
 * 
 * This module provides helper functions to check user permissions consistently
 * across the entire application.
 */

import { ROOT_EMAIL, ROLES } from '../config/constants';

export interface User {
  id: string | number;
  role: string;
  userType?: string;
  type?: string;
  empresaId?: string | number;
  sucursalId?: string | number;
  [key: string]: any;
}

/**
 * Check if user has admin privileges
 * Includes ADMIN role from Prisma schema
 */
export function isAdmin(user?: User | null): boolean {
  if (!user) return false;
  return user.role === ROLES.ADMIN;
}

/**
 * Check if user is ROOT (leonardo@finnapp.com)
 * ROOT is not a role in DB, it's a condition by email
 */
export function isRoot(user?: User | null): boolean {
  if (!user) return false;
  return user.email === ROOT_EMAIL;
}

/**
 * Check if user is an analyst
 */
export function isAnalyst(user?: User | null): boolean {
  if (!user) return false;
  return user.role === ROLES.ANALYST;
}

/**
 * Check if user is a client
 */
export function isClient(user?: User | null): boolean {
  if (!user) return false;
  return user.role === ROLES.CLIENT;
}

/**
 * Check if user is internal (Finnapp employee)
 */
export function isInternal(user?: User | null): boolean {
  if (!user) return false;
  const userType = user.userType || user.type;
  return userType === 'INTERNAL';
}

/**
 * Check if user is external (client company)
 */
export function isExternal(user?: User | null): boolean {
  if (!user) return false;
  const userType = user.userType || user.type;
  return userType === 'EXTERNAL';
}

/**
 * Check if user can access a specific feature
 * @param user - Current user
 * @param requiredRoles - List of roles that can access the feature
 * @param allowAdmins - Whether admins can bypass role checks (default: true)
 */
export function canAccess(
  user?: User | null,
  requiredRoles?: string[],
  allowAdmins: boolean = true
): boolean {
  if (!user) return false;

  // Admins can access everything if allowAdmins is true
  if (allowAdmins && isAdmin(user)) return true;

  // If no specific roles required, deny access to clients by default
  if (!requiredRoles || requiredRoles.length === 0) {
    return !isClient(user);
  }

  // Check if user's role is in the required roles
  return requiredRoles.some(role => {
    // Normalize both values for comparison
    const normalizedUserRole = user.role.toUpperCase();
    const normalizedRequiredRole = role.toUpperCase();
    
    // Check exact match
    if (normalizedUserRole === normalizedRequiredRole) return true;
    
    // Check aliases
    if (normalizedRequiredRole === 'ADMIN' && isAdmin(user)) return true;
    if (normalizedRequiredRole === 'ANALYST' && isAnalyst(user)) return true;
    if (normalizedRequiredRole === 'CLIENT' && isClient(user)) return true;
    
    return false;
  });
}

/**
 * CRITICAL SECURITY: Check if user can view datasources
 * - INTERNAL users: Can view all datasources
 * - EXTERNAL users with empresaId: Can view their company's datasources (filtered by backend)
 */
export function canViewDataSources(user?: User | null): boolean {
  if (!user) return false;
  
  // Internal users can always view datasources
  if (isInternal(user)) return true;
  
  // External users with empresaId can view their company's datasources
  // Backend filters by empresaId automatically
  if (isExternal(user) && user.empresaId) return true;
  
  return false;
}

/**
 * CRITICAL SECURITY: Check if user can manage datasources
 * - INTERNAL: Only admins and analysts
 * - EXTERNAL: Only admins with empresaId (manage their company's datasources)
 */
export function canManageDataSources(user?: User | null): boolean {
  if (!user) return false;
  
  // Internal admins and analysts can manage datasources
  if (isInternal(user) && (isAdmin(user) || isAnalyst(user))) return true;
  
  // External admins with empresaId can manage their company's datasources
  if (isExternal(user) && isAdmin(user) && user.empresaId) return true;
  
  return false;
}

/**
 * Check if user can view users management section
 * - INTERNAL: Can view all user management
 * - EXTERNAL with empresaId: Can view their company's users
 */
export function canViewUsers(user?: User | null): boolean {
  if (!user) return false;
  
  // Internal users can view all user management
  if (isInternal(user)) return true;
  
  // External users with empresaId can view their company's users
  if (isExternal(user) && user.empresaId) return true;
  
  return false;
}

/**
 * Check if user can manage internal users
 * Only ROOT can manage internal users
 */
export function canManageInternalUsers(user?: User | null): boolean {
  if (!user) return false;
  // Only ROOT (leonardo@finnapp.com) can manage internal users
  return isRoot(user);
}

/**
 * Check if user can manage external users
 * Only ADMIN can manage external users
 */
export function canManageExternalUsers(user?: User | null): boolean {
  if (!user) return false;
  // Only internal admins can manage external users
  return isInternal(user) && isAdmin(user);
}

/**
 * Check if user can manage companies
 * Only ROOT can manage companies
 */
export function canManageCompanies(user?: User | null): boolean {
  if (!user) return false;
  // Only ROOT can manage companies
  return isRoot(user);
}

/**
 * Check if user can create/edit dashboards
 * - INTERNAL: Admins and analysts
 * - EXTERNAL: Admins with empresaId
 */
export function canManageDashboards(user?: User | null): boolean {
  if (!user) return false;
  
  // Internal admins and analysts can manage dashboards
  if (isInternal(user) && (isAdmin(user) || isAnalyst(user))) return true;
  
  // External admins with empresaId can manage their company's dashboards
  if (isExternal(user) && isAdmin(user) && user.empresaId) return true;
  
  return false;
}

/**
 * Check if user can view dashboards
 */
export function canViewDashboards(user?: User | null): boolean {
  if (!user) return false;
  // Everyone can view dashboards (filtered by company)
  return true;
}

/**
 * Check if user can view activity logs
 * - INTERNAL: Only admins
 * - EXTERNAL: Admins with empresaId (their company's activity)
 */
export function canViewActivity(user?: User | null): boolean {
  if (!user) return false;
  
  // Internal admins can view all activity logs
  if (isInternal(user) && isAdmin(user)) return true;
  
  // External admins with empresaId can view their company's activity
  if (isExternal(user) && isAdmin(user) && user.empresaId) return true;
  
  return false;
}

/**
 * Check if user can view a specific company's data
 */
export function canViewCompanyData(user?: User | null, companyId?: string | number): boolean {
  if (!user) return false;
  
  // Internal admins can see everything
  if (isAdmin(user) && isInternal(user)) return true;
  
  // Users can only see their own company
  return user.empresaId === companyId;
}

/**
 * Get permissions summary for a user
 */
export function getUserPermissions(user?: User | null) {
  return {
    // View permissions
    canViewDashboards: canViewDashboards(user),
    canViewDataSources: canViewDataSources(user),
    canViewUsers: canViewUsers(user),
    canViewCompanies: canManageCompanies(user),
    canViewActivity: canViewActivity(user),
    
    // Manage permissions
    canManageDashboards: canManageDashboards(user),
    canManageDataSources: canManageDataSources(user),
    canManageInternalUsers: canManageInternalUsers(user),
    canManageExternalUsers: canManageExternalUsers(user),
    canManageCompanies: canManageCompanies(user),
    
    // User info
    isRoot: isRoot(user),
    isAdmin: isAdmin(user),
    isAnalyst: isAnalyst(user),
    isClient: isClient(user),
    isInternal: isInternal(user),
    isExternal: isExternal(user),
  };
}

/**
 * Get display name for user role (for UI display)
 * Maps Prisma enums to user-friendly Spanish names
 */
export function getRoleDisplayName(role?: string): string {
  if (!role) return 'Desconocido';
  
  const roleMap: Record<string, string> = {
    'ADMIN': 'Administrador',
    'ANALYST': 'Analista',
    'CLIENT': 'Cliente',
  };
  
  return roleMap[role] || role;
}

/**
 * Widget Permission System (Sistema de 3 niveles)
 * 
 * IMPORTANTE: Este es el sistema LEGACY para compatibilidad.
 * Para nuevo código, usar types/filters.ts
 */

export type WidgetRole = 'ADMIN' | 'ANALYST' | 'CLIENT';

export const WIDGET_ROLE_LABELS: Record<WidgetRole, string> = {
  'ADMIN': 'Administrador',
  'ANALYST': 'Analista',
  'CLIENT': 'Cliente',
};



/**
 * Verifica acceso a nivel de contexto completo (Empresa → Sucursal → Usuario)
 */
export interface FilterContext {
  empresaId: string;
  sucursalId?: string | null;
  userId: string;
  role: string;
}

/**
 * Valida que el usuario no intente acceder a datos fuera de su jerarquía
 */
export function validateHierarchyAccess(
  user: User,
  targetEmpresaId?: string,
  targetSucursalId?: string
): boolean {
  // Admin general puede todo
  if (isAdmin(user)) {
    return true;
  }

  // Validar empresa
  if (targetEmpresaId && user.empresaId !== targetEmpresaId) {
    return false;
  }

  // Validar sucursal
  if (targetSucursalId && user.sucursalId && user.sucursalId !== targetSucursalId) {
    return false;
  }

  return true;
}
